import { NavLink } from 'react-router-dom';

import { UserAvatar } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useCounts } from '@/lib/api/hooks/useCounts';
import { useAuth } from '@/pages/auth';

import { mobileBottomNav, type MobileNavItem } from './nav-items';
import { DotBadge } from './_shared';
import { userInitials } from './utils';

/**
 * Fixed bottom navigation for small screens. All six slots are uniform link
 * tiles — the raised "Create" plus-circle was retired 2026-06-08 in favour
 * of a flat Search link to the People listing page, and a Places slot was
 * added beside Friends.
 */
export function MobileBottomNav() {
  const { data: counts } = useCounts();
  const user = useAuth((s) => s.user);

  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface lg:hidden">
      <ul className="mx-auto flex max-w-md items-stretch justify-around">
        {mobileBottomNav.map((item) => {
          // The avatar slot's stored path is a placeholder — override at
          // render time to the signed-in user's profile (`/user/<username>`)
          // when we have a username, otherwise fall back to the original
          // path so a half-loaded auth state doesn't 404.
          // Avatar slot defaults to the profile route once the username
          // resolves. While it hasn't (auth still loading) keep the
          // logged-in user's session-scoped destination — /dashboard
          // rather than /dashboard/settings, which the placeholder
          // path encoded by accident.
          const resolvedItem =
            item.avatar
              ? {
                  ...item,
                  path: user?.username ? `/user/${user.username}` : '/dashboard',
                }
              : item;
          return (
            <li key={item.label} className="flex-1">
              <MobileNavSlot
                item={resolvedItem}
                count={item.badge ? counts?.[item.badge] : undefined}
                userImage={user?.image}
                userFallback={userInitials(user)}
              />
            </li>
          );
        })}
      </ul>
      {/* Safe-area padding for devices with a home indicator. */}
      <div className="h-[env(safe-area-inset-bottom)]" />
    </nav>
  );
}

interface MobileNavSlotProps {
  item: MobileNavItem;
  count: number | undefined;
  userImage?: string;
  userFallback: string;
}

function MobileNavSlot({
  item,
  count,
  userImage,
  userFallback,
}: MobileNavSlotProps) {
  const Icon = item.icon;

  return (
    <NavLink
      to={item.path ?? '#'}
      end={item.path === '/dashboard'}
      className={({ isActive }) =>
        cn(
          'flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium text-muted-foreground transition-colors',
          isActive && 'text-brand-700'
        )
      }
    >
      <span className="relative inline-flex">
        {item.avatar ? (
          <UserAvatar
            src={userImage}
            fallback={userFallback}
            alt="Your profile"
            className="h-6 w-6 text-[10px]"
          />
        ) : (
          <Icon className="h-6 w-6" />
        )}
        <DotBadge count={count} />
      </span>
      {/* The avatar slot (Profile) sits on a slightly different baseline
          than the icon slots — `-mt-[3px]` pulls its label snug against
          the avatar so the labels across the bar line up.
          `whitespace-nowrap` prevents a 6-slot bar at very narrow widths
          (e.g. iPhone SE ≈ 320px) from wrapping "Messages" / "Profile". */}
      <span
        className={cn(
          'whitespace-nowrap',
          item.avatar && '-mt-[3px]',
        )}
      >
        {item.label}
      </span>
    </NavLink>
  );
}
